import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Users, Square, Loader2 } from 'lucide-react';
import { useMeetingCapture } from '@/hooks/useMeetingCapture';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { MeetingTaskConfirmation } from '@/components/MeetingTaskConfirmation';
import { trackFeatureUsed } from '@/lib/analytics';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface TeamMeetingCaptureProps {
  contextJobId?: string;
  onMeetingProcessed?: () => void;
  variant?: 'floating' | 'inline';
}

class ConfirmationErrorBoundary extends React.Component<{ onError?: (error: any) => void; children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: any, info: any) {
    console.error('Meeting confirmation render error:', error, info);
    this.props.onError?.(error);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children as any;
  }
}

export const TeamMeetingCapture = ({ 
  contextJobId, 
  onMeetingProcessed,
  variant = 'floating' 
}: TeamMeetingCaptureProps) => {
  const { toast } = useToast();
  const {
    isRecording,
    isProcessing,
    recordingDuration,
    startRecording,
    stopRecording,
    processMeeting,
    createTasks
  } = useMeetingCapture();
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [meetingData, setMeetingData] = useState<any>(null);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleToggleRecording = async () => {
    if (isRecording) {
      // Stop and process
      const audioBlob = await stopRecording();
      
      try {
        const result = await processMeeting(audioBlob, contextJobId);
        
        console.log('Meeting result:', result);
        console.log('Proposed tasks:', result.proposedTasks);
        console.log('Meeting data:', result.meeting);
        
        // Handle cases where server already created tasks and didn't return meeting
        if (!result) {
          throw new Error('Invalid meeting data received');
        }

        if (!result.meeting) {
          const msg = result.serverCreated
            ? 'Tasks were created on the server.'
            : 'Meeting saved successfully.';
          toast({ title: 'Meeting recorded', description: msg });
          if (onMeetingProcessed) onMeetingProcessed();
          return;
        }
        
        // Tasks created by server - show success
        const taskCount = result.proposedTasks?.length ?? 0;
        toast({
          title: 'Meeting recorded',
          description: taskCount > 0 
            ? `Successfully recorded meeting with ${taskCount} task${taskCount !== 1 ? 's' : ''}.`
            : 'Meeting recorded successfully.',
        });
        if (onMeetingProcessed) onMeetingProcessed();
        return;
      } catch (error) {
        console.error('Processing error:', error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error('Error details:', errorMessage);
        toast({
          title: "Failed to process meeting",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } else {
      // Start recording
      await startRecording();
    }
  };

  const handleConfirmTasks = async (selectedTasks: any[]) => {
    try {
      const result = await createTasks(meetingData.meeting.id, selectedTasks);
      
      toast({
        title: "Tasks created successfully",
        description: `Created ${result.tasksCreated} task${result.tasksCreated !== 1 ? 's' : ''} from meeting`,
      });

      trackFeatureUsed('team_meeting_capture', { 
        tasksCreated: result.tasksCreated,
        contextJobId 
      });

      setShowConfirmation(false);
      setMeetingData(null);
      
      if (onMeetingProcessed) {
        onMeetingProcessed();
      }
    } catch (error) {
      console.error('Task creation error:', error);
      toast({
        title: "Failed to create tasks",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    }
  };

  // Fallback: if the dialog fails to mount, auto-create tasks and recover UI
  useEffect(() => {
    if (!showConfirmation || !meetingData) return;

    const timeout = window.setTimeout(async () => {
      const el = document.getElementById('meeting-confirmation');
      if (!el) {
        console.warn('Confirmation dialog failed to mount, auto-creating tasks as fallback');
        try {
          const result = await createTasks(meetingData.meeting.id, meetingData.proposedTasks || []);
          toast({
            title: 'Tasks created (fallback)',
            description: `Created ${result.tasksCreated} task${result.tasksCreated !== 1 ? 's' : ''}.`,
          });
        } catch (e) {
          console.error('Fallback task creation failed:', e);
          toast({ title: 'Could not create tasks', description: 'Please review tasks manually.', variant: 'destructive' });
        } finally {
          setShowConfirmation(false);
          setMeetingData(null);
        }
      }
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [showConfirmation, meetingData, createTasks, toast]);

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setMeetingData(null);
    
    toast({
      title: "Meeting tasks rejected",
      description: "No tasks were created",
    });
  };

  if (variant === 'inline') {
    return (
      <Button
        onClick={handleToggleRecording}
        disabled={isProcessing}
        variant={isRecording ? "destructive" : "default"}
        size="lg"
        className={cn(
          "gap-2",
          isRecording && "animate-pulse"
        )}
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Processing...
          </>
        ) : isRecording ? (
          <>
            <Square className="h-5 w-5" />
            Stop Recording ({formatDuration(recordingDuration)})
          </>
        ) : (
          <>
            <Users className="h-5 w-5" />
            Team Meeting
          </>
        )}
      </Button>
    );
  }

  // Floating variant
  return (
    <ConfirmationErrorBoundary onError={(e) => {
      console.error('TeamMeetingCapture render error:', e);
      toast({
        title: 'Error rendering meeting UI',
        description: 'Recovered from error. Please try again.',
        variant: 'destructive',
      });
    }}>
    <>
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation && !!meetingData} onOpenChange={(open) => {
        if (!open) handleCancelConfirmation();
      }}>
        <DialogContent className="p-0 max-w-4xl">
          {meetingData && (
            <ConfirmationErrorBoundary
              onError={(e) => {
                console.error('Failed to render MeetingTaskConfirmation:', e);
                setShowConfirmation(false);
                setMeetingData(null);
                toast({
                  title: 'Could not open confirmation',
                  description: 'Tasks may have been created. Review the job board to verify.',
                  variant: 'destructive',
                });
              }}
            >
              <MeetingTaskConfirmation
                embedded
                summary={meetingData.meeting.summary}
                proposedTasks={meetingData.proposedTasks}
                jobs={meetingData.jobs}
                people={meetingData.people}
                onConfirm={handleConfirmTasks}
                onCancel={handleCancelConfirmation}
              />
            </ConfirmationErrorBoundary>
          )}
        </DialogContent>
      </Dialog>

      <div className="fixed bottom-[5.5rem] right-4 md:bottom-20 z-50 flex flex-col items-end gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleToggleRecording}
                disabled={isProcessing}
                className={cn(
                  "h-10 sm:h-14 rounded-full shadow-lg transition-all flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4",
                  isRecording 
                    ? "bg-red-500 hover:bg-red-600 animate-pulse" 
                    : "bg-[#D6B77A] hover:bg-[#C5A669]"
                )}
                style={{ backgroundColor: isRecording ? undefined : '#D6B77A' }}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin text-white" />
                    <span className="text-white text-xs sm:text-sm font-semibold">Processing...</span>
                  </>
                ) : isRecording ? (
                  <>
                    <Square className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    <span className="text-white text-xs sm:text-sm font-semibold">Stop Meeting</span>
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    <span className="text-white text-xs sm:text-sm font-semibold">Team Meeting</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <p className="font-semibold mb-1">Team Meeting Recorder (unlimited length)</p>
              <p className="text-xs text-muted-foreground">
                Use for site walks, coordination meetings, or client discussions. The AI transcribes the entire conversation and automatically creates multiple tasks from the discussion, capturing decisions, action items, and deliverables.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Recording indicator */}
      {isRecording && (
        <div className="fixed bottom-60 right-4 z-40 bg-background border border-border rounded-lg px-4 py-2 shadow-lg">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-medium">
              Recording: {formatDuration(recordingDuration)}
            </span>
          </div>
        </div>
      )}

      {/* Processing indicator removed to prevent full-screen blank overlay */}
      {/* We still show button state and recording badge above */}
    </>
    </ConfirmationErrorBoundary>
  );
};